import { Link } from "react-router-dom";
import {
  GraduationCap,
  Twitter,
  Linkedin,
  Github,
  Instagram,
  Mail,
  Phone,
  MapPin,
  Facebook,
  Youtube,
  ArrowRight,
  Heart,
} from "lucide-react";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { name: "About Us", href: "/about" },
    { name: "Courses", href: "/courses" },
    { name: "Mentorship", href: "/mentorship" },
    { name: "Community", href: "/forums" },
    { name: "Careers", href: "/careers" },
  ];

  const supportLinks = [
    { name: "Help Center", href: "/docs" },
    { name: "Contact Us", href: "/contact" },
    { name: "Terms of Service", href: "/terms" },
    { name: "Privacy Policy", href: "/privacy" },
    { name: "About Us", href: "/about" },
  ];

  const socialLinks = [
    {
      name: "Twitter",
      icon: Twitter,
      href: "https://twitter.com",
      color: "hover:bg-[#1DA1F2]",
    },
    {
      name: "LinkedIn",
      icon: Linkedin,
      href: "https://linkedin.com",
      color: "hover:bg-[#0A66C2]",
    },
    {
      name: "GitHub",
      icon: Github,
      href: "https://github.com",
      color: "hover:bg-[#333]",
    },
    {
      name: "Instagram",
      icon: Instagram,
      href: "https://instagram.com",
      color: "hover:bg-[#E4405F]",
    },
    {
      name: "Facebook",
      icon: Facebook,
      href: "https://facebook.com",
      color: "hover:bg-[#1877F2]",
    },
    {
      name: "YouTube",
      icon: Youtube,
      href: "https://youtube.com",
      color: "hover:bg-[#FF0000]",
    },
  ];

  return (
    <footer className="text-white bg-gray-900">
      {/* Main Footer */}
      <div className="px-4 pt-12 pb-8 mx-auto max-w-7xl sm:px-6 lg:px-8 md:pt-16">
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-4 lg:gap-12">
          {/* Brand Column */}
          <div className="space-y-4">
            <Link
              to="/"
              onClick={() => setIsMobileMenuOpen(false)}
              className="flex items-center gap-2"
            >
              <div className="flex items-center gap-0 font-extrabold">
                <span className="text-[16px] bg-orange-500 rounded-l-lg font-bold text-white  transition-all  pl-2 py-0.5 ">
                  Learn
                </span>
                <span className="text-[16px] font-bold bg-blue-500 text-white transition-all rounded-r-lg pr-2 py-0.5 ">
                  Hub
                </span>
              </div>
            </Link>
            <p className="text-sm leading-relaxed text-gray-400">
              Empowering learners worldwide with quality education and expert
              mentorship. Transform your career with industry-ready skills.
            </p>
            <div className="flex flex-wrap gap-2">
              {socialLinks.map((social) => {
                const Icon = social.icon;
                return (
                  <a
                    key={social.name}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`p-2 bg-gray-800 rounded-lg transition-all duration-300 ${social.color} hover:scale-110 hover:shadow-lg`}
                    aria-label={social.name}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Quick Links Column */}
          <div>
            <h3 className="relative inline-block mb-4 text-lg font-semibold">
              Quick Links
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></span>
            </h3>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="flex items-center gap-1 text-gray-400 transition-colors duration-200 hover:text-primary group"
                  >
                    <ArrowRight className="w-3 h-3 transition-all duration-200 -translate-x-2 opacity-0 group-hover:opacity-100 group-hover:translate-x-0" />
                    <span className="transition-transform duration-200 group-hover:translate-x-1">
                      {link.name}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Support Column */}
          <div>
            <h3 className="relative inline-block mb-4 text-lg font-semibold">
              Support
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></span>
            </h3>
            <ul className="space-y-2">
              {supportLinks.map((link) => (
                <li key={link.name}>
                  <Link
                    to={link.href}
                    className="flex items-center gap-1 text-gray-400 transition-colors duration-200 hover:text-primary group"
                  >
                    <ArrowRight className="w-3 h-3 transition-all duration-200 opacity-0 group-hover:opacity-100" />
                    <span>{link.name}</span>
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Column */}
          <div>
            <h3 className="relative inline-block mb-4 text-lg font-semibold">
              Contact Info
              <span className="absolute -bottom-2 left-0 w-12 h-0.5 bg-primary rounded-full"></span>
            </h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-gray-400 transition-colors duration-200 hover:text-primary group">
                <Mail className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="text-sm">ndzelenromarick@gmail.com</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 transition-colors duration-200 hover:text-primary group">
                <Phone className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="text-sm">+(237) 676 154 253</span>
              </li>
              <li className="flex items-start gap-3 text-gray-400 transition-colors duration-200 hover:text-primary group">
                <MapPin className="w-5 h-5 flex-shrink-0 mt-0.5 group-hover:scale-110 transition-transform" />
                <span className="text-sm">Bambili, Cameroon</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        {/* <div className="pt-8 mt-12 border-t border-gray-800">
          <div className="grid items-center gap-8 md:grid-cols-2">
            <div>
              <h3 className="mb-2 text-lg font-semibold">Subscribe to Our Newsletter</h3>
              <p className="text-sm text-gray-400">
                Get the latest updates on new courses, events, and learning resources.
              </p>
            </div>
            <div>
              <form className="flex flex-col gap-3 sm:flex-row">
                <input
                  type="email"
                  placeholder="Enter your email address"
                  className="flex-1 px-4 py-2.5 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-transparent transition-all"
                />
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-gradient-to-r from-primary to-orange-500 text-white font-medium rounded-lg hover:shadow-lg hover:shadow-primary/30 transition-all duration-300 hover:scale-105"
                >
                  Subscribe
                </button>
              </form>
              <p className="mt-2 text-xs text-gray-500">
                We respect your privacy. Unsubscribe at any time.
              </p>
            </div>
          </div>
        </div> */}

        {/* Bottom Bar */}
        <div className="pt-6 mt-8 border-t border-gray-800">
          <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
            <div className="text-center md:text-left">
              <p className="text-sm text-gray-400">
                &copy; {currentYear} LearnHub. All rights reserved.
              </p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link
                to="/terms"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                Terms
              </Link>
              <span className="text-gray-700">|</span>
              <Link
                to="/privacy"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                Privacy
              </Link>
              <span className="text-gray-700">|</span>
              <Link
                to="/cookies"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                Cookies
              </Link>
              <span className="text-gray-700">|</span>
              <Link
                to="/docs"
                className="text-gray-400 transition-colors hover:text-primary"
              >
                Accessibility
              </Link>
            </div>
            <div className="flex items-center gap-1 text-xs text-gray-500">
              <span>Made with</span>
              <Heart className="w-3 h-3 text-red-500 fill-red-500 animate-pulse" />
              <span>for learners worldwide</span>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative gradient line at top */}
      <div className="h-1 opacity-50 bg-gradient-to-r from-primary via-orange-500 to-primary"></div>
    </footer>
  );
};

export default Footer;
